import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useCreateBedarf } from '../../api/bedarfe';
import { BedarfeForm } from '../../components/bedarfe';
import { buildBedarfFormValues } from '../../components/bedarfe/bedarfFormValues';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { CreateBedarfDto } from '../../types/bedarf';
import {
  draftKey,
  readDraft,
  removeDraft,
  sameFormValues,
  writeDraft,
  type FormDraft,
} from '../../utils/formDraft';

const DRAFT_FORM = 'bedarf-neu';

const formatSavedAt = (iso: string) =>
  new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

export function BedarfCreatePage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();
  const createMutation = useCreateBedarf();

  // Whatever was typed is kept in the browser on every change, so closing the
  // tab, a reload or an accidental back gesture does not lose it.
  const key = user ? draftKey(DRAFT_FORM, user.id) : null;
  const [restored, setRestored] = useState<FormDraft<CreateBedarfDto> | null>(() =>
    key ? readDraft<CreateBedarfDto>(key) : null,
  );
  /** Bumped to remount the form empty after the draft is thrown away. */
  const [formGeneration, setFormGeneration] = useState(0);
  const hasInput = useRef(restored !== null);
  const emptyValues = useMemo(() => buildBedarfFormValues(), []);

  const handleValuesChange = useCallback(
    (values: CreateBedarfDto) => {
      if (!key) return;
      // An emptied form is no draft — otherwise "Abbrechen" would ask about
      // nothing and the next visit would restore an empty page.
      if (sameFormValues({ ...values }, { ...emptyValues })) {
        removeDraft(key);
        hasInput.current = false;
        return;
      }
      writeDraft(key, values);
      hasInput.current = true;
    },
    [key, emptyValues],
  );

  const discardDraft = () => {
    if (key) removeDraft(key);
    hasInput.current = false;
    setRestored(null);
    setFormGeneration((generation) => generation + 1);
  };

  const handleDiscard = async () => {
    const confirmed = await confirm({
      title: 'Entwurf verwerfen?',
      message: 'Alle Eingaben in diesem Formular werden gelöscht.',
      confirmLabel: 'Verwerfen',
      cancelLabel: 'Behalten',
      variant: 'danger',
    });
    if (confirmed) discardDraft();
  };

  const handleSubmit = async (data: CreateBedarfDto) => {
    try {
      const result = await createMutation.mutateAsync(data);
      // Only a successful save retires the draft; a failed one keeps it.
      if (key) removeDraft(key);
      toast.success('Bedarf erfolgreich erstellt');
      navigate(`/bedarfe/${result.data.id}`);
    } catch (error) {
      console.error('Failed to create bedarf:', error);
      toast.error('Fehler beim Erstellen des Bedarfs');
    }
  };

  const handleCancel = async () => {
    if (hasInput.current) {
      // Dismissing the dialog keeps the draft: the safe answer is the one that
      // loses nothing.
      const verwerfen = await confirm({
        title: 'Eingaben verwerfen?',
        message:
          'Der Bedarf ist noch nicht gespeichert. Der Entwurf bleibt in diesem Browser erhalten, wenn Sie ihn behalten.',
        confirmLabel: 'Verwerfen',
        cancelLabel: 'Entwurf behalten',
        variant: 'warning',
      });
      if (verwerfen && key) removeDraft(key);
    }
    navigate('/bedarfe');
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2 text-sm text-gray-500">
            <li><Link to="/bedarfe" className="hover:text-gray-700">Bedarfe</Link></li>
            <li>/</li>
            <li className="text-gray-900">Neuer Bedarf</li>
          </ol>
        </nav>
        <h1 className="text-2xl font-bold text-gray-900">Neuen Bedarf erstellen</h1>
        <p className="mt-1 text-sm text-gray-500">
          Erfassen Sie einen neuen Bedarf für Schulbegleitung
        </p>
      </div>

      {restored && (
        <div
          role="status"
          data-testid="entwurf-hinweis"
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3"
        >
          <p className="text-sm text-amber-800">
            Entwurf vom {formatSavedAt(restored.savedAt)} wiederhergestellt.
          </p>
          <button
            type="button"
            onClick={handleDiscard}
            className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
          >
            Entwurf verwerfen
          </button>
        </div>
      )}

      <BedarfeForm
        key={formGeneration}
        draftValues={restored?.values}
        onValuesChange={handleValuesChange}
        footerHint={key ? 'Eingaben werden als Entwurf in diesem Browser gespeichert.' : undefined}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={createMutation.isPending}
      />
    </div>
  );
}
